import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import { useEffect, useState } from "react";
import useButtonStyles from "../../styles/useButtonStyles";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { APIConstants } from "../../constants/ApiConstants";
import { constant } from "../../constants";
import BasicAutoComplete from "../ui/BasicAutoComplete";
import Loader from "../ui/Loader";
import BasicTextField from "../forms/formElements/BasicTextField";
import useFormStyles from "../../styles/useFormStyles";
import CheckBoxSection from "../forms/formElements/CheckBoxSection";
import { UpdateReceiptRejectionList } from "../../static-models/RejectedReasonList";
import BasicModal from "../ui/BasicModal";
import SimpleImageSlider from "../receipts/ImageSlider";
import { dateConstants } from "../../constants/dateConstants";
import ActivityLogTable from "../forms/activityLogTable";
import formatDates from "../../util/dateFormat";
import useSameDaySameStore from "../../Hooks/receipts/useSameDaySameStore";
import FraudDetectWarning from "../ui/FraudDetectWarning";
import useSameMonthSameStore from "../../Hooks/receipts/useSameMonthSameStore";
import useAccumulativeSpendingSameMonth from "../../Hooks/receipts/useAccumulateSpendingSameMonth";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { receiptsActions } from "../../redux/slices/recieptsSlice";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SelectInputField from "../forms/formElements/SelectInputField";
import {
  getReceiptFraudCheck,
  getReceipts,
  getPendingReceipts,
} from "../../services/ApiServices/Receipts";
import { filterConstants } from "../../constants/AppConstants";
import { usePermissionGetter } from "../../Hooks/Permissions/userPermissionGetter";
import useActiveMerchants from "../../Hooks/merchant/useActiveMerchants";

interface UpdatePropsType {
  closeDrawer?: Function;
  onRefresh?: () => void;
  receipt?: any;
  module?: string;
}

const FRAUD_BYPASS_RULES = [
  {
    id: "DAILY_RECEIPTS_PER_STORE",
    label: "Multiple transactions from one store within the same day",
    configKey: "multiple_receipts_store",
  },
  {
    id: "MONTHLY_RECEIPTS_PER_STORE",
    label:
      "Multiple transactions from one store within a calendar month",
    configKey: "multiple_receipts_calender",
  },
  {
    id: "SINGLE_RECEIPT_AMOUNT",
    label: "Transaction amount is greater than (S$)",
    configKey: "receipt_amount",
  },
  {
    id: "MONTHLY_CUMULATIVE_SPEND",
    label: "Accumulated spending within a calendar month is over (S$)",
    configKey: "spending_amount",
  },
] as const;

const getFraudBypassRuleIds = (receipt: any): string[] => {
  const requestedRuleIds =
    receipt?.requested_fraud_bypass_rule_ids ??
    receipt?.fraudBypassRuleIds ??
    receipt?.fraud_bypass_rule_ids ??
    receipt?.fraudBypassRules ??
    [];
  const bypassedRuleIds =
    receipt?.fraud_decision?.bypassedRuleIds ?? receipt?.bypassedRuleIds ?? [];
  const ruleIds = [
    ...(Array.isArray(requestedRuleIds) ? requestedRuleIds : []),
    ...(Array.isArray(bypassedRuleIds) ? bypassedRuleIds : []),
  ];

  return Array.from(new Set(ruleIds));
};

const getActiveFraudBypassRuleIds = (receipt: any): string[] => {
  const bypasses = receipt?.activeFraudBypasses;
  if (!Array.isArray(bypasses)) return [];

  return bypasses
    .map((bypass: any) =>
      typeof bypass === "string" ? bypass : bypass?.ruleId ?? bypass?.id,
    )
    .filter((ruleId: string | undefined): ruleId is string => Boolean(ruleId));
};

const getBypassedRuleIdsFromFraudCheck = (rules: any): string[] => {
  const bypassedRules = Array.isArray(rules?.bypassedRules)
    ? rules.bypassedRules.map((rule: any) =>
        typeof rule === "string" ? rule : rule?.ruleId,
      )
    : [];
  const bypassedRuleIds = Array.isArray(rules?.bypassedRuleIds)
    ? rules.bypassedRuleIds
    : [];

  return Array.from(
    new Set(
      [...bypassedRules, ...bypassedRuleIds].filter(
        (ruleId: string | undefined): ruleId is string => Boolean(ruleId),
      ),
    ),
  );
};

const UpdateMemberReceipts = (props: UpdatePropsType) => {
  const { getUserPermission: getReceiptPermission } = usePermissionGetter(
    "receipts",
    "receipts",
  );
  const { closeDrawer, receipt, onRefresh, module } = props;
  const dispatch = useAppDispatch();

  const formStyles = useFormStyles();
  const buttonStyles = useButtonStyles();
  const [rotationAngle, setRotationAngle] = useState(0);
  const [receiptData, setReceiptData] = useState<any>({});
  const [merchantCategory, setMerchantCategory] = useState<any>({});
  const [image, setImage] = useState<HTMLElement | null>(null);
  const [updateReceiptFormState, setUpdateReceiptFormState] = useState<any>({
    merchant: "",
    member: "",
    joinDate: "",
    loyaltyCardNo: "",
    merchantType: "",
    receiptNumber: "",
    adminAmount: "",
    paymentType: "",
    partner: "",
    dateOfPurchase: "",
    submittedDate: "",
    status: "",
    approvalCheckList: "",
    promoCode: "",
    rejectionReason: "",
    rejectionSubReason: "",
    processingReason: "",
    over20Dollar: false,
    within48Hours: false,
    validMerchant: false,
    dateOfPurchaseEntered: false,
    remarks: "",
  });
  const [accept, setAccept] = useState(false);
  const [reject, setReject] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [receiptActive, setReceiptActive] = useState(false);
  const [clearForm, setClearForm] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [axiosErrorMessage, setAxiosErrorMessage] = useState<string>(
    "Something went wrong",
  );

  const [uploadReceiptLoading, setUploadReceiptLoading] = useState(false);
  const [memberData, setMemberData] = useState<any>({});
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [merchant, setMerchant] = useState<any>("");
  const [receiptStatus, setReceiptStatus] = useState<string>("");
  const [receiptStatusValue, setReceiptStatusValue] = useState<string>("");
  const [loadingMember, setLoadingMember] = useState<boolean>(false);
  const [paymentType, setPaymentType] = useState<string>("");
  const [memberEmail, setMemberEmail] = useState<string>("");
  const [memberLoyalty, setMemberLoyalty] = useState<string>("");
  const [billingDate, setBillingData] = useState<Dayjs | null>(
    updateReceiptFormState?.dateOfPurchase
      ? updateReceiptFormState?.dateOfPurchase
      : null,
  );
  const [uploadLog, setUploadLog] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [amount, setAmount] = useState<string>("");
  const [partner, setPartner] = useState<string[]>([]);
  const [fraudDetectMessage, setFraudDetectMessage] = useState<string[]>();
  const [selectedFraudBypassRuleIds, setSelectedFraudBypassRuleIds] = useState<
    string[]
  >([]);
  const [activeFraudBypassRuleIds, setActiveFraudBypassRuleIds] = useState<
    string[]
  >([]);
  const theme = useTheme();
  const navigate = useNavigate();
  const firstName = useAppSelector(
    (state: any) => state.persistReducers.login.firstName,
  );
  const lastName = useAppSelector(
    (state: any) => state.persistReducers.login.lastName,
  );
  const reviewedEmail = useAppSelector(
    (state: any) => state.persistReducers.login.email,
  );
  const reviewdBy = `${firstName} ${lastName}`;

  const paymentTypes: any[] = [
    {
      id: "1",
      label: "Card",
      type: "Card",
    },
    {
      id: "2",
      label: "Cash",
      type: "Cash",
    },
    {
      id: "3",
      label: "Other",
      type: "Other",
    },
  ];
  const columns: any[] = [
    {
      label: "Reason",
      key: "reason",
      capitalize: false,
    },
    {
      label: "Status",
      key: "status",
      capitalize: true,
    },
    {
      label: "Created by",
      key: "createdBy",
      capitalize: false,
    },
    {
      label: "Created Date",
      key: "createdAt",
      capitalize: false,
    },
  ];

  const csPortalPartnerValidation =
    constant.domain === process.env.REACT_APP_CSA_DOMAIN &&
    (updateReceiptFormState.status === filterConstants.Reject.toLowerCase() ||
      updateReceiptFormState.status === filterConstants.Accept.toLowerCase());
  const isPendingReceipt = module === "pending-receipts";
  const showFraudBypassChecklist =
    (isPendingReceipt ||
      receiptData?.receipt_type?.toLowerCase() === "soundbox") &&
    !(isPendingReceipt && receiptStatusValue === filterConstants.Reject);
  const filterMerchantList = useActiveMerchants();
  const merchantListFromReduxFilter = filterMerchantList?.map((entity: any) => {
    return {
      id: entity.id,
      label: entity.name,
      type: entity.type,
      sub_category: entity.sub_category,
    };
  });
  useEffect(() => {
    if (accept) {
      setUpdateReceiptFormState((prev: any) => {
        return {
          ...prev,
          status: filterConstants.Accept.toLowerCase(),
        };
      });
    }
    if (reject) {
      setUpdateReceiptFormState((prev: any) => {
        return {
          ...prev,
          status: filterConstants.Reject.toLowerCase(),
        };
      });
    }
    if (processing) {
      setUpdateReceiptFormState((prev: any) => {
        return {
          ...prev,
          status: filterConstants.Processing.toLowerCase(),
        };
      });
    }
  }, [accept, reject, processing]);

  useEffect(() => {
    if (showAcceptList) {
      setShowAcceptList(false);
      setReceiptStatusValue("");
    }
  }, [updateReceiptFormState.adminAmount, updateReceiptFormState.merchant]);

  const rotateRight = () => {
    setRotationAngle((prevAngle) => prevAngle + 90);
  };

  const rotateLeft = () => {
    setRotationAngle((prevAngle) => prevAngle - 90);
  };

  function ZoomIn() {
    if (image) {
      let width = image.clientWidth;
      let height = image.clientHeight;
      image.style.width = width + 50 + "px";
      image.style.height = height + 50 + "px";
    }
  }
  function ZoomOut() {
    if (image) {
      let width = image.clientWidth;
      let height = image.clientHeight;
      image.style.width = width - 50 + "px";
      image.style.height = height - 50 + "px";
    }
  }

  const handleClickAccept = async (event: any) => {
    if (event.target.nodeName === "LABEL") {
      return;
    }
    try {
      const reseponseData = await getFraudDetect();
      setFraudDetectMessage(
        Array.isArray(reseponseData) ? reseponseData : reseponseData?.data,
      );
      if (module === "pending-receipts") {
        const bypassedRuleIds = getBypassedRuleIdsFromFraudCheck(
          reseponseData?.rules,
        );
        if (bypassedRuleIds.length) {
          setActiveFraudBypassRuleIds((currentRuleIds) =>
            Array.from(new Set([...currentRuleIds, ...bypassedRuleIds])),
          );
        }
      }
      setAccept(true);
    } catch (error: any) {
      setAccept(false);
      setReceiptStatusValue("");
      if (error?.response?.status === 401) return;
      setAxiosErrorMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Fraud evaluation failed",
      );
      setUploadError(true);
      setShowModal(true);
    }
  };

  const getPurchaseDateMs = () => {
    const source = billingDate ?? updateReceiptFormState.dateOfPurchase;
    if (source == null || source === "") return undefined;
    if (typeof source === "number") return source;
    if (dayjs.isDayjs(source)) return source.valueOf();
    const parsed = new Date(source).getTime();
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const getFraudDetect = async () => {
    const intialMerchant = getMerchant(merchant, "label");
    const formMerchantData = updateReceiptFormState.merchant || intialMerchant;
    const merchantData = merchantListFromReduxFilter?.filter(
      (list: any) => list?.label === formMerchantData,
    )[0];
    const isPendingSoundbox = module === "pending-receipts";
    const reseponseData = await getReceiptFraudCheck({
      merchantId: merchantData?.id,
      amount: updateReceiptFormState.adminAmount,
      purchaseDateMs: getPurchaseDateMs(),
      receiptId: isPendingSoundbox
        ? undefined
        : receiptData?.id || receiptsId,
      memberId: isPendingSoundbox
        ? memberEmail || updateReceiptFormState.member
        : undefined,
      loyaltyCardNo: isPendingSoundbox
        ? receiptData?.loyalty_card_no
        : undefined,
    });
    return reseponseData;
  };

  const handleCloseAccept = () => {
    setAccept(false);
    setShowAcceptList(false);
    setShowRejectList(false);
    setShowProcessing(false);
    setReceiptStatusValue("");
  };
  const handleClickReject = (event: any) => {
    if (event.target.nodeName === "LABEL") {
      return;
    }
    setReject(true);
  };

  const handleCloseReject = () => {
    setReject(false);
    setShowAcceptList(false);
    setShowRejectList(false);
    setShowProcessing(false);
    setReceiptStatusValue("");
  };
  const handleClickProcessing = (event: any) => {
    if (event.target.nodeName === "LABEL") {
      return;
    }
    setProcessing(true);
  };

  const handleCloseProcessing = () => {
    setProcessing(false);
    setShowAcceptList(false);
    setShowRejectList(false);
    setShowProcessing(false);
    setReceiptStatusValue("");
  };
  const [showAcceptList, setShowAcceptList] = useState(false);

  const handleShowAcceptList = () => {
    setShowAcceptList(true);
    setAccept(false);
    setShowRejectList(false);
    setShowProcessing(false);
  };
  const [showRejectList, setShowRejectList] = useState(false);

  const handleShowRejectList = () => {
    setShowRejectList(true);
    setReject(false);
    setShowAcceptList(false);
    setShowProcessing(false);
  };
  const [showProcessing, setShowProcessing] = useState(false);

  const handleShowProcessing = () => {
    setShowProcessing(true);
    setReject(false);
    setShowAcceptList(false);
    setShowRejectList(false);
    setProcessing(false);
  };

  const params = useParams();
  const { receiptsId } = params;

  const loadComponent = async () => {
    setShowAcceptList(false);
    setShowRejectList(false);
    setShowProcessing(false);
    setLoadingData(true);
    const receiptId: string = receiptsId ? receiptsId : receipt;
    await fetchReceipt(receiptId);
    setLoadingData(false);
    setLoadingMember(true);
  };
  const fraudSettings = useAppSelector(
    (state: any) => state.persistReducers.getReceiptsList.fraudSettings,
  );
  useEffect(() => {
    loadComponent();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL + APIConstants.partnerSelection,
      );
      setPartner(response.data.data);
    } catch (error) {
      console.error("Error fetching partners:", error);
    }
  };
  useEffect(() => {
    fetchPartners();
  }, []);
  const fetchMemberReceipts = async (email: string) => {
    try {
      let queryParams = `filters=member:eq:${email}`;
      queryParams += `&module=${module}&feature=receipts`;
      const responseData = await getReceipts(queryParams);
      const { result } = responseData;
      const data = result?.[0]?._source;
      dispatch(
        receiptsActions.updateMemberReceipts({
          memberReceipts: data,
        }),
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setAmount(updateReceiptFormState.amount_bpo);
  }, [updateReceiptFormState]);

  const formatDOBDate = (date: string) => {
    const dobDate = new Date(date);
    return (
      dobDate.getFullYear() +
      "-" +
      (dobDate.getMonth() + 1).toString().padStart(2, "0") +
      "-" +
      dobDate.getDate().toString().padStart(2, "0")
    );
  };

  const createActivityLog = (data: any) => {
    const activityLog = data.upload_log.map((log: any) => {
      return {
        status: log.status,
        createdAt: log.upload_time ? formatDates(log.upload_time) : "",
        createdBy: log.created_by,
        comment: log?.notes ? log?.notes : " - ",
        reason: log?.rejection_reason
          ? log?.rejection_reason +
            (log?.rejection_sub_reason ? " - " + log?.rejection_sub_reason : "")
          : log?.notes
            ? log?.notes
            : "-",
      };
    });
    setActivityLog(activityLog);
  };

  const fetchReceipt = async (receiptId: string) => {
    try {
      setLoading(true);

      if (module === "pending-receipts") {
        // For pending receipts, use transactionUUID filter
        const queryURL = `filters=transactionUUID%3Aeq%3A${receiptId}`;

        const pendingReceiptResponse = await getPendingReceipts(queryURL);

        // Handle different response structures
        const result =
          pendingReceiptResponse?.result ||
          pendingReceiptResponse?.data?.result;

        if (!result || result.length === 0) {
          throw new Error("No pending receipt found with the given ID");
        }

        const pendingReceiptData = result[0]?._source;

        if (!pendingReceiptData) {
          throw new Error("Invalid pending receipt data structure");
        }

        // Fetch member details using loyalty card number
        let memberDetails: any = null;
        if (pendingReceiptData.loyaltyCardNo) {
          try {
            const memberResponse = await axios.get(
              `${
                process.env.REACT_APP_API_URL +
                APIConstants.getCustomer +
                "?externalId=" +
                pendingReceiptData.loyaltyCardNo
              }`,
            );

            memberDetails = memberResponse?.data?.data?.data;
          } catch (error) {
            console.error("Error fetching member details:", error);
          }
        }

        // Enrich pending receipt data with member details
        const enrichedReceiptData = {
          ...pendingReceiptData,
          id: pendingReceiptData.receiptID,
          member_name: memberDetails?.first_name
            ? memberDetails?.first_name
            : "-",

          member_join_date: memberDetails?.join_date
            ? memberDetails?.join_date
            : "-",
          loyalty_card_no: pendingReceiptData.loyaltyCardNo,

          receipt_number: pendingReceiptData.receiptNumber,
          receipt_status: pendingReceiptData.status,
          amount_member: pendingReceiptData.amount,

          merchant: pendingReceiptData.merchantName || "",

          upload_log: pendingReceiptData.upload_log || [],
          eVouchers: pendingReceiptData.eVouchers,
          physicalVouchers: pendingReceiptData.physicalVouchers,
          redemptions: pendingReceiptData.redemptions,
        };

        createActivityLog(enrichedReceiptData);
        setReceiptData(enrichedReceiptData);
        setSelectedFraudBypassRuleIds(
          getFraudBypassRuleIds(pendingReceiptData),
        );
        setActiveFraudBypassRuleIds(
          getActiveFraudBypassRuleIds(pendingReceiptData),
        );
        setMerchant(pendingReceiptData.merchantId);
        setReceiptStatus(pendingReceiptData.status);

        setUpdateReceiptFormState((prev: any) => {
          return {
            ...prev,
            merchant: pendingReceiptData.merchantName,

            member: memberDetails?.email || pendingReceiptData.userId,
            loyaltyCardNumber: pendingReceiptData.loyaltyCardNo,
            joinDate: memberDetails?.createdAt
              ? new Date(memberDetails.createdAt).toDateString()
              : new Date(pendingReceiptData.createdAt).toDateString(),
            receiptNumber: pendingReceiptData.receiptNumber,
            adminAmount: pendingReceiptData.amount,
            submittedDate: new Date(
              pendingReceiptData.createdAt,
            ).toDateString(),
            status: pendingReceiptData.status,
            remark: "-",
            eVouchers: pendingReceiptData.eVouchers,
            physicalVouchers: pendingReceiptData.physicalVouchers,
            redemptions: pendingReceiptData.redemptions,
            dateOfPurchase: pendingReceiptData?.timestamp
              ? pendingReceiptData?.timestamp
              : null,
          };
        });

        // Don't fetch member receipts for pending receipts to avoid errors
        // await fetchMemberReceipts(pendingReceiptData.userId);
      } else {
        // For regular receipts, use id filter
        let queryURL = `filters=id:eq:${receiptId}`;
        queryURL += `&module=${module}&feature=receipts`;
        const regularReceiptResponse = await getReceipts(queryURL);
        const { result } = regularReceiptResponse;
        const regularReceiptData = result?.[0]?._source;

        createActivityLog(regularReceiptData);
        setReceiptData(regularReceiptData);
        setSelectedFraudBypassRuleIds(
          getFraudBypassRuleIds(regularReceiptData),
        );
        setActiveFraudBypassRuleIds(
          getActiveFraudBypassRuleIds(regularReceiptData),
        );
        setMerchant(regularReceiptData.merchant);
        setMerchantCategory(regularReceiptData.merchant_type);
        setReceiptStatus(regularReceiptData.receipt_status);
        setPaymentType(regularReceiptData.payment_type);
        setUploadLog(regularReceiptData.upload_log);

        setUpdateReceiptFormState((prev: any) => {
          const updatedState = {
            ...prev,
            merchant: regularReceiptData.merchant,
            member: regularReceiptData.member,
            loyaltyCardNumber: regularReceiptData.loyalty_card_no,
            joinDate: new Date(regularReceiptData.createdAt).toDateString(),
            receiptNumber: regularReceiptData.receipt_number,
            adminAmount:
              regularReceiptData.amount_bpo === 0
                ? regularReceiptData.amount_member
                : regularReceiptData.amount_bpo,
            submittedDate: new Date(
              regularReceiptData.createdAt,
            ).toDateString(),
            status: regularReceiptData.receipt_status,
            paymentType: regularReceiptData.payment_type,
            partner: regularReceiptData.partner,
            update_eligible: regularReceiptData?.update_eligible,
            reviewed_date: regularReceiptData?.reviewed_date,
            remark: regularReceiptData.remarks
              ? regularReceiptData.remarks
              : "-",
          };
          if (regularReceiptData.date_of_purchase !== dateConstants.emptyDate) {
            updatedState.dateOfPurchase = regularReceiptData.date_of_purchase;
          }
          return updatedState;
        });

        await fetchMemberReceipts(regularReceiptData.member);
      }

      setLoading(false);
      setError(false);
    } catch (error) {
      console.error("Error in fetchReceipt:", error);
      setAxiosErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to fetch receipt details",
      );
      setUploadError(true);
      setShowModal(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    const merchantObj = merchantListFromReduxFilter.filter(
      (merchant: any) => merchant.label === updateReceiptFormState.merchant,
    )[0];

    if (merchantObj?.sub_category) {
      // Handle sub_category - if it's an object, extract the name/label, otherwise use as is
      const subCategory =
        typeof merchantObj.sub_category === "object"
          ? merchantObj.sub_category?.name ||
            merchantObj.sub_category?.label ||
            JSON.stringify(merchantObj.sub_category)
          : merchantObj.sub_category;
      setMerchantCategory(subCategory);
    } else if (module === "pending-receipts") {
      // For pending receipts without sub_category, set empty or default value
      setMerchantCategory("");
    }
    if (merchantObj?.id) {
      setMerchant(merchantObj?.id);
    }
    if (updateReceiptFormState?.paymentType) {
      setPaymentType(updateReceiptFormState?.paymentType);
    }
    // if (updateReceiptFormState?.partner) {
    //   setPaymentType(updateReceiptFormState?.partner);
    // }
  }, [updateReceiptFormState]);

  useEffect(() => {
    (async () => {
      if (updateReceiptFormState.member) await fetchMember();
      let image = document.getElementById("image");
      if (image) {
        setImage(image);
      }
      setLoadingMember(false);
    })();
  }, [receiptData]);

  useEffect(() => {
    if (updateReceiptFormState?.dateOfPurchase)
      setBillingData(updateReceiptFormState?.dateOfPurchase);
  }, [updateReceiptFormState?.dateOfPurchase]);

  const submitHandler = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    const billDate: any = billingDate;
    if (
      constant.domain === process.env.REACT_APP_ADMIN_DOMAIN &&
      !receiptStatusValue
    ) {
      setAxiosErrorMessage("Please select status");
      setUploadError(true);
      setShowModal(true);
      return;
    }
    if (module !== "pending-receipts") {
      if (!billingDate) {
        setAxiosErrorMessage("Please select billing date");
        setUploadError(true);
        setShowModal(true);
      } else if (new Date(billDate) > new Date()) {
        setAxiosErrorMessage("Billing Date cannot be in the future");
        setUploadError(true);
        setShowModal(true);
      } else {
        const payload = generateUpdateReceiptpayload();
        await updateReceipt(payload);
        await loadComponent();
        if (onRefresh) onRefresh();
      }
    } else if (module === "pending-receipts") {
      const status =
        updateReceiptFormState.status === "accept"
          ? "approved"
          : updateReceiptFormState.status === "reject"
            ? "rejected"
            : "pending";
      const payload: any = {
        transactionId: receiptData?.transactionUUID,
        status: status,
        rejectionReason: updateReceiptFormState.rejectionReason,
        rejectionSubReason: updateReceiptFormState.rejectionSubReason
          ? updateReceiptFormState.rejectionSubReason
          : "",
        billAmount: Number(updateReceiptFormState.adminAmount),
        fraudBypassRuleIds: selectedFraudBypassRuleIds,
      };
      await updateSoundboxPendingReceipt(payload);
      await loadComponent();
      if (onRefresh) onRefresh();
    }
  };

  const generateUpdateReceiptpayload = () => {
    const dateOfPurchase = new Date(updateReceiptFormState.dateOfPurchase);
    const merchantId = merchantListFromReduxFilter.filter(
      (merchant: any) => merchant.label === updateReceiptFormState.merchant,
    )[0]?.id;
    const payload: any = {
      memberId: receiptData.latestEmail ? receiptData.latestEmail : "",
      loyalty_card_number: receiptData.loyalty_card_no
        ? receiptData.loyalty_card_no
        : "",
      receiptId: receiptData.id,
      merchant: merchantId ? merchantId : merchant,
      merchantType: updateReceiptFormState.merchantType,
      receiptNumber: updateReceiptFormState.receiptNumber,
      amountAdmin: updateReceiptFormState.adminAmount.toString(),
      billingDate: billingDate,
      remarks: updateReceiptFormState.remarks,
    };

    // Only add paymentType and partner for non-pending receipts

    payload.paymentType = updateReceiptFormState.paymentType
      ? updateReceiptFormState.paymentType
      : paymentType;
    payload.partner = updateReceiptFormState.partner
      ? updateReceiptFormState.partner
      : partner;

    if (constant.domain === process.env.REACT_APP_ADMIN_DOMAIN) {
      delete payload.partner;
    }
    if (updateReceiptFormState.status !== receiptData.receipt_status) {
      payload.status = updateReceiptFormState.status;
    } else {
      delete payload.status;
    }

    switch (updateReceiptFormState.status) {
      case filterConstants.Accept.toLowerCase():
        payload.approvalList = getApprovalCheckList();
        payload.promoCode = updateReceiptFormState.promoCode;
        break;
      case filterConstants.Reject.toLowerCase():
        payload.rejectionReason = updateReceiptFormState.rejectionReason;
        payload.rejectionSubReason = updateReceiptFormState.rejectionSubReason;
        break;
      case filterConstants.Processing.toLowerCase():
      case filterConstants.Pending.toLowerCase():
        payload.processingReason = updateReceiptFormState.processingReason;
        break;
      default:
        break;
    }

    return payload;
  };

  const getApprovalCheckList = () => {
    const checkList = [];
    if (updateReceiptFormState.over20Dollar) {
      checkList.push("Over S$20");
    }
    if (updateReceiptFormState.within48Hours) {
      checkList.push("Within 48 hours");
    }
    if (updateReceiptFormState.dateOfPurchaseEntered) {
      checkList.push("Date of Purchase Entered");
    }
    if (updateReceiptFormState.validMerchant) {
      checkList.push("Valid Merchant");
    }
    return checkList.map((element) => `'${element}'`).join(",");
  };

  const fetchMember = async () => {
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL +
          APIConstants.getCustomer +
          "?externalId=" +
          updateReceiptFormState.loyaltyCardNumber
        }`,
      );
      const member = response?.data?.data?.data;
      setMemberData(member);
      setMemberLoyalty(member.loyaltyId);
      setMemberEmail(member.email);
    } catch (error) {
      console.error(error);
    }
  };

  const updateReceipt = async (payload: any) => {
    setUploadReceiptLoading(true);
    try {
      const newPayload = {
        ...payload,
        reviewdBy,
        reviewedEmail,
        module: "receipts",
        feature: "receipts",
      };
      const response = await axios.patch(
        process.env.REACT_APP_API_URL + APIConstants.receiptApi,
        newPayload,
      );
      setUploadError(false);
      setShowModal(true);
      setAxiosErrorMessage("Something went wrong");
      setUploadReceiptLoading(false);
      setReceiptStatusValue("");
    } catch (error: any) {
      setUploadReceiptLoading(false);
      setAxiosErrorMessage(
        error?.response?.data?.error
          ? error?.response?.data?.error
          : "Something went wrong",
      );
      setUploadError(true);
      setShowModal(true);
      setReceiptStatusValue("");
    } finally {
      // setTimeout(() => {
      //   navigate(-1);
      // }, 3000);
    }
  };
  const updateSoundboxPendingReceipt = async (payload: any) => {
    setUploadReceiptLoading(true);
    try {
      const payloadStatus = APIConstants.approvePendingReceipts;

      const response: any = await axios.post(
        process.env.REACT_APP_API_URL_PHASE_2 + payloadStatus,
        payload,
      );

      if (response.data.status == "success") {
        setUploadError(false);
        setShowModal(true);
        setAxiosErrorMessage("Something went wrong");
        setUploadReceiptLoading(false);
        setReceiptStatusValue("");
      } else if (response.data.status == "failed") {
        setUploadError(true);
        setShowModal(true);
        setAxiosErrorMessage(response.data.message || "Something went wrong");
        setUploadReceiptLoading(false);
        setReceiptStatusValue("");
      } else if (response.data.success === false) {
        setUploadError(true);
        setShowModal(true);
        setAxiosErrorMessage(response.data.message || "Something went wrong");
        setUploadReceiptLoading(false);
        setReceiptStatusValue("");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setUploadReceiptLoading(false);
    }
  };

  const onCloseReceiptModule = () => {
    closeDrawer && closeDrawer(null);
  };
  const getMerchant = (id: any, type: string) => {
    const merchant = merchantListFromReduxFilter.filter(
      (list: any) => list.id === id,
    )[0];

    if (type === "label") {
      return merchant?.label;
    }
    return merchant?.type;
  };

  const disableFormUpdate = () => {
    if (
      receiptStatus === filterConstants.Appeal.toLowerCase() ||
      receiptStatus === filterConstants.Pending.toLowerCase() ||
      receiptStatus === filterConstants.Processing.toLowerCase() ||
      (constant.domain === process.env.REACT_APP_CSA_DOMAIN &&
        updateReceiptFormState.status ===
          filterConstants.Accept.toLowerCase()) ||
      (updateReceiptFormState?.reviewed_date === 0 &&
        !updateReceiptFormState?.update_eligible)
    )
      return false;
    else return !updateReceiptFormState?.update_eligible;
  };

  const getSubReason = () => {
    const selectedReasonObject = UpdateReceiptRejectionList.filter(
      (element: any) => element.type === updateReceiptFormState.rejectionReason,
    );
    if (!selectedReasonObject || !selectedReasonObject[0]?.subCategory)
      return <></>;
    return (
      <Box mb={2} width="100%">
        <BasicAutoComplete
          required={true}
          label={"Rejection Sub Reason"}
          setState={setUpdateReceiptFormState}
          stateKey="rejectionSubReason"
          secondaryStateKey=""
          options={selectedReasonObject[0]?.subCategory}
          clear={clearForm}
        />
      </Box>
    );
  };

  return receiptActive ? (
    <>
      <Paper
        sx={{
          minHeight: "100%",
          backgroundColor: theme.color?.searchBackground,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: theme.font?.fontFamily.primary,
            fontSize: theme.font?.fontSize.large,
            fontWeight: theme.font?.fontWeight.thick,
          }}
        >
          Receipt is already active with another user
        </Typography>
      </Paper>
      {setTimeout(() => {
        closeDrawer && closeDrawer(null);
      }, 3000)}
    </>
  ) : (
    <Grid minHeight={"100%"}>
      <Grid
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        px={6}
        py={3}
        height={"15vh"}
      >
        <Typography
          sx={{
            fontFamily: theme.font?.fontFamily.primary,
            fontSize: theme.font?.fontSize.large,
            fontWeight: theme.font?.fontWeight.thick,
          }}
        >
          Receipt Details
        </Typography>
        <IconButton
          aria-label="delete"
          onClick={
            closeDrawer
              ? () => {
                  closeDrawer(null);
                }
              : () => {}
          }
        >
          <CloseIcon />
        </IconButton>
      </Grid>

      {loadingData ? (
        <Grid display={"flex"} justifyContent={"center"} pt={5}>
          <Loader />
        </Grid>
      ) : (
        <Grid height={"85vh"} padding={3} px={4} pb={0} overflow={"auto"}>
          <Grid
            border={theme.border?.newGreyBorder}
            borderRadius={"12px"}
            display={"flex"}
            height={"50vh"}
            overflow={"hidden"}
          >
            <Grid
              width={"50%"}
              height={"70%"}
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"space-between"}
              p={3}
              py={6}
            >
              {
                <Grid display={"flex"}>
                  <Typography
                    width={"40%"}
                    sx={{
                      fontFamily: theme.font?.fontFamily.primary,
                      fontWeight: theme.font?.fontWeight.slightThick,
                      fontSize: theme.font?.fontSize.tableHeader,
                      color: theme.color?.newTextColor,
                    }}
                  >
                    Member :
                  </Typography>
                  <Typography
                    width={"60%"}
                    sx={{
                      fontFamily: theme.font?.fontFamily.primary,
                      fontWeight: theme.font?.fontWeight.thick,
                      fontSize: theme.font?.fontSize.medium,
                      color: theme.color?.newTextColor,
                    }}
                  >
                    {receiptData.member_name ? receiptData.member_name : "-"}
                  </Typography>
                </Grid>
              }
              {
                <Grid display={"flex"}>
                  <Typography
                    width={"40%"}
                    sx={{
                      fontFamily: theme.font?.fontFamily.primary,
                      fontWeight: theme.font?.fontWeight.slightThick,
                      fontSize: theme.font?.fontSize.tableHeader,
                      color: theme.color?.newTextColor,
                    }}
                  >
                    Join Date:
                  </Typography>
                  <Typography
                    width={"60%"}
                    sx={{
                      fontFamily: theme.font?.fontFamily.primary,
                      fontWeight: theme.font?.fontWeight.thick,
                      fontSize: theme.font?.fontSize.medium,
                      color: theme.color?.newTextColor,
                    }}
                  >
                    {receiptData.member_join_date
                      ? new Date(receiptData.member_join_date).toDateString()
                      : "-"}
                  </Typography>
                </Grid>
              }
              <Grid display={"flex"}>
                <Typography
                  width={"40%"}
                  sx={{
                    fontFamily: theme.font?.fontFamily.primary,
                    fontWeight: theme.font?.fontWeight.slightThick,
                    fontSize: theme.font?.fontSize.tableHeader,
                    color: theme.color?.newTextColor,
                  }}
                >
                  Loyalty Card No:
                </Typography>
                <Typography
                  width={"60%"}
                  sx={{
                    fontFamily: theme.font?.fontFamily.primary,
                    fontWeight: theme.font?.fontWeight.thick,
                    fontSize: theme.font?.fontSize.medium,
                    color: theme.color?.newTextColor,
                  }}
                >
                  {receiptData.loyalty_card_no
                    ? receiptData.loyalty_card_no
                    : "-"}
                </Typography>
              </Grid>

              {/* {updateReceiptFormState.eVouchers &&
                updateReceiptFormState.eVouchers.length > 0 && (
                  <Grid display={"flex"}>
                    <Typography
                      width={"40%"}
                      sx={{
                        fontFamily: theme.font?.fontFamily.primary,
                        fontWeight: theme.font?.fontWeight.slightThick,
                        fontSize: theme.font?.fontSize.tableHeader,
                        color: theme.color?.newTextColor,
                      }}
                    >
                      eVoucher code:
                    </Typography>
                    <Typography
                      width={"60%"}
                      sx={{
                        fontFamily: theme.font?.fontFamily.primary,
                        fontWeight: theme.font?.fontWeight.thick,
                        fontSize: theme.font?.fontSize.medium,
                        color: theme.color?.newTextColor,
                      }}
                    >
                      {updateReceiptFormState.eVouchers.find(
                        (eVoucher: any) => eVoucher.code
                      )?.code || "-"}
                    </Typography>
                  </Grid>
                )}
              {updateReceiptFormState.eVouchers &&
                updateReceiptFormState.eVouchers.length > 0 && (
                  <Grid display={"flex"}>
                    <Typography
                      width={"40%"}
                      sx={{
                        fontFamily: theme.font?.fontFamily.primary,
                        fontWeight: theme.font?.fontWeight.slightThick,
                        fontSize: theme.font?.fontSize.tableHeader,
                        color: theme.color?.newTextColor,
                      }}
                    >
                      eVoucher Amount:
                    </Typography>
                    <Typography
                      width={"60%"}
                      sx={{
                        fontFamily: theme.font?.fontFamily.primary,
                        fontWeight: theme.font?.fontWeight.thick,
                        fontSize: theme.font?.fontSize.medium,
                        color: theme.color?.newTextColor,
                      }}
                    >
                      {updateReceiptFormState.eVouchers.find(
                        (eVoucher: any) => eVoucher.value
                      )?.value || "-"}
                    </Typography>
                  </Grid>
                )} */}
              <Grid display={"flex"}>
                <Typography
                  width={"40%"}
                  sx={{
                    fontFamily: theme.font?.fontFamily.primary,
                    fontWeight: theme.font?.fontWeight.slightThick,
                    fontSize: theme.font?.fontSize.tableHeader,
                    color: theme.color?.newTextColor,
                  }}
                >
                  Submission Date:
                </Typography>
                <Typography
                  width={"60%"}
                  sx={{
                    fontFamily: theme.font?.fontFamily.primary,
                    fontWeight: theme.font?.fontWeight.thick,
                    fontSize: theme.font?.fontSize.medium,
                    color: theme.color?.newTextColor,
                  }}
                >
                  {updateReceiptFormState.submittedDate
                    ? updateReceiptFormState.submittedDate
                    : "-"}
                </Typography>
              </Grid>
              <Grid display={"flex"}>
                <Typography
                  width={"40%"}
                  sx={{
                    fontFamily: theme.font?.fontFamily.primary,
                    fontWeight: theme.font?.fontWeight.slightThick,
                    fontSize: theme.font?.fontSize.tableHeader,
                    color: theme.color?.newTextColor,
                  }}
                >
                  Status:
                </Typography>
                <Typography
                  width={"60%"}
                  sx={{
                    fontWeight: "500",
                    fontFamily: theme?.font?.fontFamily?.primary,
                    color:
                      (receiptStatus === filterConstants.Accept.toLowerCase() &&
                        theme?.color?.success) ||
                      (receiptStatus === filterConstants.Reject.toLowerCase() &&
                        theme?.color?.failure) ||
                      (receiptStatus ===
                        filterConstants.Pending.toLowerCase() &&
                        theme?.color?.secondary) ||
                      theme?.color?.dark,
                  }}
                >
                  {receiptStatus ? receiptStatus?.toLocaleUpperCase() : "-"}
                </Typography>
              </Grid>
            </Grid>
            <Grid width={"50%"}>
              <Grid
                display={"flex"}
                flexDirection={"column"}
                height={"100%"}
                p={2}
              >
                <Grid height={"10%"}>
                  <Typography
                    sx={{
                      fontFamily: theme.font?.fontFamily.primary,
                      fontSize: theme.font?.fontSize.tableHeader,
                      fontWeight: theme.font?.fontWeight.thick,
                      color: theme.color?.newFilterGrey,
                    }}
                  >
                    Receipt Image
                  </Typography>
                </Grid>
                <Grid height={"100%"} width={"100%"} overflow={"hidden"} mb={2}>
                  {Object.keys(receiptData).length !== 0 ? (
                    <SimpleImageSlider
                      images={
                        receiptData?.upload_log
                          ?.filter((x: any) => x.receipt_image)
                          .map((x: any) => x.receipt_image) || []
                      }
                      rotationAngle={rotationAngle}
                      setImage={setImage}
                    />
                  ) : (
                    <Loader />
                  )}
                </Grid>

                <Grid
                  display={"flex"}
                  justifyContent={"space-between"}
                  gap={1}
                  alignSelf={"end"}
                  width={"100%"}
                  height={"10%"}
                >
                  <Grid>
                    <IconButton onClick={ZoomIn}>
                      <ZoomInIcon />
                    </IconButton>
                    <IconButton onClick={ZoomOut}>
                      <ZoomOutIcon />
                    </IconButton>
                  </Grid>

                  <IconButton onClick={rotateRight}>
                    <RefreshOutlinedIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <form onSubmit={submitHandler}>
            {module !== "pending-receipts" && (
              <Grid
                display={"flex"}
                gap={4}
                py={3}
                borderBottom={theme.border?.newGreyBorder}
              >
                <Grid width={"30%"}>
                  <BasicAutoComplete
                    required={true}
                    label={"Merchant *"}
                    setState={setUpdateReceiptFormState}
                    stateKey="merchant"
                    secondaryStateKey="merchantType"
                    options={merchantListFromReduxFilter}
                    clear={clearForm}
                    defaultValue={
                      getMerchant(merchant, "label") ||
                      receiptData?.merchant_name
                    }
                    readonly={disableFormUpdate() || csPortalPartnerValidation}
                  />
                </Grid>
                <Grid width={"30%"}>
                  <BasicTextField
                    setState={setUpdateReceiptFormState}
                    placeholder=""
                    defaultValue={merchantCategory}
                    stateKey="merchantType"
                    variant="outlined"
                    label="Directory Subcategory"
                    align="left"
                    required={true}
                    readonly={true}
                    disableSubmit={() => {}}
                    clear={clearForm}
                  />
                </Grid>
              </Grid>
            )}
            <Grid display={"flex"} flexDirection={"column"} py={3} pb={0}>
              <Grid display={"flex"} gap={4} alignItems={"center"} py={1}>
                {
                  <Grid width={"30%"}>
                    <BasicTextField
                      setState={setUpdateReceiptFormState}
                      placeholder=""
                      defaultValue={
                        Number(updateReceiptFormState.adminAmount) === 0
                          ? "0"
                          : updateReceiptFormState.adminAmount
                      }
                      stateKey="adminAmount"
                      variant="outlined"
                      label="Receipt Amount (S$)"
                      align="left"
                      type="number"
                      required={true}
                      disableSubmit={() => {}}
                      clear={clearForm}
                      readonly={
                        disableFormUpdate() || csPortalPartnerValidation
                      }
                      allowZeroValue={
                        receiptData?.receipt_type?.toLowerCase() ==
                          "soundbox" || module === "pending-receipts"
                          ? true
                          : false
                      }
                    />
                  </Grid>
                }
                {
                  <Grid width={"30%"}>
                    <FormLabel
                      sx={{
                        ...formStyles.formLabel,
                        textAlign: "left",
                      }}
                    >
                      {"Billing Date *"}
                    </FormLabel>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={["DatePicker", "DatePicker"]}>
                        <DateTimePicker
                          disableFuture
                          format="MM/DD/YYYY hh:mm a"
                          sx={{
                            width: "100%",
                            size: "small",
                            "& .MuiInputBase-input": {
                              padding: "9px",
                            },
                          }}
                          value={
                            updateReceiptFormState?.dateOfPurchase
                              ? dayjs(updateReceiptFormState?.dateOfPurchase)
                              : null
                          }
                          onChange={(newDate) => setBillingData(newDate)}
                          disabled={
                            module !== "pending-receipts"
                              ? disableFormUpdate() || csPortalPartnerValidation
                              : true
                          }
                        />
                      </DemoContainer>
                    </LocalizationProvider>
                  </Grid>
                }
              </Grid>
              <Grid
                display={"flex"}
                gap={4}
                pt={1}
                pb={4}
                borderBottom={theme.border?.newGreyBorder}
              >
                {
                  <Grid width={"30%"}>
                    <BasicTextField
                      setState={setUpdateReceiptFormState}
                      defaultValue={updateReceiptFormState.receiptNumber}
                      placeholder=""
                      stateKey="receiptNumber"
                      variant="outlined"
                      label="Scanned Receipt Number *"
                      align="left"
                      required={true}
                      disableSubmit={() => {}}
                      clear={clearForm}
                      readonly={
                        module !== "pending-receipts"
                          ? disableFormUpdate() || csPortalPartnerValidation
                          : true
                      }
                      // readonly={true}
                    />
                  </Grid>
                }
                {module === "pending-receipts" && (
                  <Grid width={"30%"}>
                    <BasicTextField
                      setState={setUpdateReceiptFormState}
                      defaultValue={updateReceiptFormState.merchant}
                      placeholder=""
                      stateKey="merchant"
                      variant="outlined"
                      label="Merchant Name"
                      align="left"
                      required={true}
                      disableSubmit={() => {}}
                      clear={clearForm}
                      readonly
                      // readonly={true}
                    />
                  </Grid>
                )}
                {module !== "pending-receipts" && (
                  <Grid width={"30%"}>
                    <BasicAutoComplete
                      required={true}
                      label="Payment Type *"
                      setState={setUpdateReceiptFormState}
                      stateKey="paymentType"
                      secondaryStateKey="paymentType"
                      options={paymentTypes}
                      clear={clearForm}
                      defaultValue={updateReceiptFormState?.paymentType}
                      readonly={
                        disableFormUpdate() || csPortalPartnerValidation
                      }
                    />
                  </Grid>
                )}
                {constant.domain === process.env.REACT_APP_CSA_DOMAIN && (
                  <Grid width={"30%"}>
                    <Typography sx={{ ...formStyles.formLabel }}>
                      Partner *
                    </Typography>
                    <SelectInputField
                      defaultValue={updateReceiptFormState?.partner}
                      setState={setUpdateReceiptFormState}
                      stateKey="partner"
                      label=""
                      options={partner}
                      align="left"
                    />
                  </Grid>
                )}
              </Grid>
              {/* <Grid
                borderBottom={theme.border?.newGreyBorder}
                display={"flex"}
                py={3}
                gap={4}
              >
                <Grid width={"64%"}>
                  <BasicTextField
                    setState={setUpdateReceiptFormState}
                    defaultValue={
                      updateReceiptFormState.remark ??
                      updateReceiptFormState.remarks
                    }
                    placeholder=""
                    stateKey="remarks"
                    variant="outlined"
                    label="Remark"
                    align="left"
                    multiline
                    rows={3}
                    clear={clearForm}
                    readonly={disableFormUpdate() || csPortalPartnerValidation}
                  />
                </Grid>
              </Grid> */}
              <Grid
                border={theme.border?.newGreyBorder}
                borderRadius={"12px"}
                display={"flex"}
                py={3}
                my={3}
                gap={4}
              >
                <Grid
                  width={"50%"}
                  display={"flex"}
                  flexDirection={"column"}
                  pl={3}
                >
                  <Grid display={"flex"} gap={2} alignItems={"center"}>
                    <Typography
                      sx={{
                        fontFamily: theme.font?.fontFamily.primary,
                        fontWeight: theme.font?.fontWeight.slightThick,
                        fontSize: theme.font?.fontSize.tableHeader,
                        color: theme.color?.newTextColor,
                      }}
                    >
                      Status:
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "500",
                        fontFamily: theme?.font?.fontFamily?.primary,
                        color:
                          (receiptStatus ===
                            filterConstants.Accept.toLowerCase() &&
                            theme?.color?.success) ||
                          (receiptStatus ===
                            filterConstants.Reject.toLowerCase() &&
                            theme?.color?.failure) ||
                          (receiptStatus ===
                            filterConstants.Pending.toLowerCase() &&
                            theme?.color?.secondary) ||
                          theme?.color?.dark,
                      }}
                    >
                      {receiptStatus ? receiptStatus?.toLocaleUpperCase() : "-"}
                    </Typography>
                  </Grid>
                  <Grid>
                    {constant.domain === process.env.REACT_APP_ADMIN_DOMAIN && (
                      <Box width="100%">
                        <FormControl>
                          <RadioGroup
                            onChange={(e) => {
                              setReceiptStatusValue(e.target.value);
                            }}
                            value={receiptStatusValue}
                            row
                            aria-labelledby="demo-radio-buttons-group-label"
                            defaultValue={filterConstants.Accept.toLowerCase()}
                            name="radio-buttons-group"
                            sx={{
                              display: "flex",
                              color: "red",
                              "&:focus MuiFormLabel-root": {
                                color: "red",
                              },
                            }}
                          >
                            <FormControlLabel
                              disabled={
                                receiptStatus ===
                                  filterConstants.Accept.toLowerCase() ||
                                disableFormUpdate()
                              }
                              onClick={handleClickAccept}
                              value={filterConstants.Accept}
                              control={
                                <Radio
                                  sx={{
                                    "&, &.Mui-checked": {
                                      color: theme?.color?.secondary,
                                    },
                                  }}
                                />
                              }
                              label={
                                <FormLabel
                                  sx={{
                                    ...formStyles.formLabel,
                                    textAlign: "left",
                                    "& .MuiFormLabel-root": {
                                      color: theme?.color?.secondary,
                                    },
                                  }}
                                >
                                  Accept
                                </FormLabel>
                              }
                            />
                            <Dialog
                              open={accept}
                              onClose={handleClickAccept}
                              aria-labelledby="draggable-dialog-title"
                            >
                              <Alert severity="warning">
                                Are you sure you want to approve this receipt
                                and award loyalty points to the member?
                              </Alert>

                              <Box
                                display="flex"
                                justifyContent="center"
                                mt={2}
                                px={4}
                                pb={4}
                                flexDirection={"column"}
                              >
                                <Grid>
                                  {fraudDetectMessage?.length ? (
                                    <FraudDetectWarning
                                      warnings={fraudDetectMessage}
                                    />
                                  ) : (
                                    <></>
                                  )}
                                </Grid>
                                <Box
                                  display={"flex"}
                                  justifyContent={"center"}
                                  pt={1}
                                  gap={2}
                                >
                                  <Button
                                    onClick={handleCloseAccept}
                                    variant="outlined"
                                    color="error"
                                    sx={{
                                      width: "30%",
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    onClick={handleShowAcceptList}
                                    variant="contained"
                                    color="success"
                                    sx={{
                                      width: "30%",
                                      display: "flex",
                                      marginRight: "10px",
                                    }}
                                  >
                                    Ok
                                  </Button>
                                </Box>
                              </Box>
                            </Dialog>
                            <FormControlLabel
                              disabled={
                                receiptStatus ===
                                  filterConstants.Reject.toLowerCase() ||
                                disableFormUpdate()
                              }
                              onClick={handleClickReject}
                              value={filterConstants.Reject}
                              control={
                                <Radio
                                  sx={{
                                    "&, &.Mui-checked": {
                                      color: theme?.color?.secondary,
                                    },
                                  }}
                                />
                              }
                              label={
                                <FormLabel
                                  sx={{
                                    ...formStyles.formLabel,
                                    textAlign: "left",
                                    "&:MuiFormLabel-root.Mui-focused": {
                                      color: "red",
                                    },
                                  }}
                                >
                                  Reject
                                </FormLabel>
                              }
                            />
                            <Dialog
                              open={reject}
                              onClose={handleCloseReject}
                              aria-labelledby="draggable-dialog-title"
                            >
                              <Alert severity="warning">
                                Are you sure you want to reject this receipt?
                              </Alert>

                              <Box
                                display="flex"
                                justifyContent="center"
                                mt={2}
                                p={4}
                                gap={2}
                              >
                                <Button
                                  onClick={handleCloseReject}
                                  variant="outlined"
                                  color="error"
                                  sx={{
                                    display: "flex",
                                    width: "30%",
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  variant="contained"
                                  color="success"
                                  onClick={handleShowRejectList}
                                  sx={{
                                    width: "30%",
                                    display: "flex",
                                    marginRight: "10px",
                                  }}
                                >
                                  Ok
                                </Button>
                              </Box>
                            </Dialog>

                            {module !== "pending-receipts" && (
                              <FormControlLabel
                                disabled={disableFormUpdate()}
                                onClick={handleClickProcessing}
                                value={filterConstants.Processing}
                                control={
                                  <Radio
                                    sx={{
                                      "&, &.Mui-checked": {
                                        color: theme?.color?.secondary,
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <FormLabel
                                    sx={{
                                      ...formStyles.formLabel,
                                      textAlign: "left",
                                      "&:focus MuiFormLabel": {
                                        color: "red",
                                      },
                                    }}
                                  >
                                    Processing
                                  </FormLabel>
                                }
                              />
                            )}
                            {module !== "pending-receipts" && (
                              <Dialog
                                open={processing}
                                onClose={handleCloseProcessing}
                                aria-labelledby="draggable-dialog-title"
                              >
                                <Alert severity="warning">
                                  Are you sure you want to add this receipt to
                                  processing status?
                                </Alert>

                                <Box
                                  display="flex"
                                  justifyContent="center"
                                  mt={2}
                                  p={4}
                                  gap={2}
                                >
                                  <Button
                                    onClick={handleCloseProcessing}
                                    variant="outlined"
                                    color="error"
                                    sx={{
                                      display: "flex",
                                      width: "30%",
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleShowProcessing}
                                    sx={{
                                      width: "30%",
                                      display: "flex",
                                      marginRight: "10px",
                                    }}
                                  >
                                    Ok
                                  </Button>
                                </Box>
                              </Dialog>
                            )}
                          </RadioGroup>
                        </FormControl>
                      </Box>
                    )}
                  </Grid>
                </Grid>
                <Grid width={"50%"} px={2}>
                  {module !== "pending-receipts" && showAcceptList && (
                    <Grid display={"flex"} flexDirection={"column"} gap={2}>
                      <Typography
                        sx={{
                          fontFamily: theme.font?.fontFamily.primary,
                          fontSize: theme.font?.fontSize.tableHeader,
                          fontWeight: theme.font?.fontWeight.slightThick,
                        }}
                      >
                        Receipt approval checklist
                      </Typography>
                      <Grid item>
                        <CheckBoxSection
                          direction="column"
                          setState={setUpdateReceiptFormState}
                          items={[
                            {
                              stateKey: "over20Dollar",
                              value: "Over S$20",
                              checked: updateReceiptFormState.over20Dollar,
                              required: true,
                            },
                            {
                              stateKey: "within48Hours",
                              value: "Within 48 hours",
                              checked: updateReceiptFormState.within48Hours,
                              required: true,
                            },
                            {
                              stateKey: "validMerchant",
                              value: "Valid Merchant",
                              checked: updateReceiptFormState.validMerchant,
                              required: true,
                            },
                            {
                              stateKey: "dateOfPurchaseEntered",
                              value: "Date of Purchase Entered",
                              checked:
                                updateReceiptFormState.dateOfPurchaseEntered,
                              required: true,
                            },
                          ]}
                        />
                      </Grid>
                    </Grid>
                  )}
                  {showRejectList && (
                    <>
                      <Box mb={2} width="100%">
                        <BasicAutoComplete
                          required={true}
                          label={"Rejection Reason *"}
                          setState={setUpdateReceiptFormState}
                          stateKey="rejectionReason"
                          secondaryStateKey=""
                          options={UpdateReceiptRejectionList}
                          clear={clearForm}
                        />
                      </Box>
                      {getSubReason()}
                    </>
                  )}
                  {module !== "pending-receipts" && showProcessing && (
                    <BasicTextField
                      setState={setUpdateReceiptFormState}
                      placeholder=""
                      stateKey="processingReason"
                      variant="outlined"
                      label="Processing Reason *"
                      align="left"
                      required={true}
                      disableSubmit={() => {}}
                      clear={clearForm}
                    />
                  )}
                </Grid>
              </Grid>
              {showFraudBypassChecklist && (
                <Grid
                  border={theme.border?.newGreyBorder}
                  borderRadius={"12px"}
                  display={"flex"}
                  flexDirection={"column"}
                  px={3}
                  py={2}
                  mb={3}
                  gap={1}
                >
                  <Typography
                    sx={{
                      fontFamily: theme.font?.fontFamily.primary,
                      fontSize: theme.font?.fontSize.tableHeader,
                      fontWeight: theme.font?.fontWeight.slightThick,
                    }}
                  >
                    Fraud rules bypass checklist
                  </Typography>
                  {FRAUD_BYPASS_RULES.map((rule) => {
                    const isActiveBypass = activeFraudBypassRuleIds.includes(
                      rule.id,
                    );
                    const checked =
                      isActiveBypass ||
                      selectedFraudBypassRuleIds.includes(rule.id);
                    const configValue = fraudSettings?.[rule.configKey];

                    return (
                      <FormControlLabel
                        key={rule.id}
                        disabled={
                          isActiveBypass ||
                          module !== "pending-receipts" ||
                          disableFormUpdate()
                        }
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={(event) => {
                              setSelectedFraudBypassRuleIds((currentRuleIds) =>
                                event.target.checked
                                  ? [...currentRuleIds, rule.id]
                                  : currentRuleIds.filter(
                                      (ruleId) => ruleId !== rule.id,
                                    ),
                              );
                            }}
                            size="small"
                            sx={{
                              color: theme?.color?.secondary,
                              "&.Mui-checked": {
                                color: theme?.color?.secondary,
                              },
                            }}
                          />
                        }
                        label={`${rule.label} : ${configValue ?? "-"}`}
                      />
                    );
                  })}
                </Grid>
              )}
              <Grid py={2} mb={5}>
                {module !== "pending-receipts" && (
                  <ActivityLogTable
                    columns={columns}
                    data={activityLog}
                    headerText={""}
                  />
                )}
              </Grid>
              <Grid
                borderTop={theme.border?.newGreyBorder}
                height={"6.25rem"}
                display={"flex"}
                justifyContent={"end"}
                gap={2}
                alignItems={"center"}
              >
                <Button
                  sx={{
                    ...buttonStyles.outlinedSecondary,
                    width: "25%",
                  }}
                  onClick={
                    closeDrawer
                      ? () => {
                          closeDrawer(false);
                        }
                      : () => {}
                  }
                >
                  Cancel
                </Button>
                {getReceiptPermission("update") && (
                  <Button
                    sx={{
                      ...buttonStyles.containedSecondary,
                      color: theme.color?.background,
                      width: "25%",
                    }}
                    variant="contained"
                    type="submit"
                    disabled={
                      disableFormUpdate() //||
                      // (constant.domain === process.env.REACT_APP_CSA_DOMAIN &&
                      //   updateReceiptFormState.status ===
                      //     filterConstants.Reject.toLowerCase()) ||
                      // (constant.domain === process.env.REACT_APP_CSA_DOMAIN &&
                      //   updateReceiptFormState.status ===
                      //     filterConstants.Accept.toLowerCase())
                    }
                  >
                    Save
                  </Button>
                )}
              </Grid>
            </Grid>
          </form>
        </Grid>
      )}
      {showModal &&
        (uploadError ? (
          <BasicModal
            title={axiosErrorMessage}
            success={false}
            setState={setShowModal}
            onCloseModule={onCloseReceiptModule}
          />
        ) : (
          <BasicModal
            title={"Receipt Review Successful!"}
            success={true}
            setState={setShowModal}
            onCloseModule={onCloseReceiptModule}
          />
        ))}
      {uploadReceiptLoading && <BasicModal content={<Loader />} />}
    </Grid>
  );
};

export default UpdateMemberReceipts;
